import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { ChevronDown, ChevronRight, Package, Trash2, BrushCleaning } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";

const STATUSES = [
  { value: "pending", label: "In afwachting", color: "#facc15" },
  { value: "paid", label: "Betaald", color: "#22c55e" },
  { value: "shipped", label: "Verzonden", color: "#3b82f6" },
  { value: "delivered", label: "Geleverd", color: "#a3a3a3" },
  { value: "cancelled", label: "Geannuleerd", color: "#ef4444" },
];

const PAYMENT_STATUS_META = {
  paid: { label: "BETAALD", color: "#22c55e" },
  awaiting_payment: { label: "WACHT OP BETALING", color: "#facc15" },
  failed: { label: "MISLUKT", color: "#ef4444" },
  cancelled: { label: "GEANNULEERD", color: "#9ca3af" },
  expired: { label: "VERLOPEN", color: "#9ca3af" },
};

const FILTERS = [
  { key: "all", label: "Alles" },
  { key: "paid", label: "Betaald" },
  { key: "awaiting_payment", label: "Wacht op betaling" },
  { key: "junk", label: "Onbetaald (annul/verlopen/mislukt)" },
];

const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];

const AdminOrders = () => {
  const { authHeaders } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");
  const [cleaning, setCleaning] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/orders`, { headers: authHeaders })
      .then((r) => setOrders(r.data))
      .catch(() => toast.error("Bestellingen laden mislukt"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [authHeaders]);

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    if (filter === "junk")
      return orders.filter((o) =>
        ["cancelled", "expired", "failed"].includes(o.payment_status)
      );
    return orders.filter((o) => o.payment_status === filter);
  }, [orders, filter]);

  const counts = useMemo(() => {
    const c = { all: orders.length, paid: 0, awaiting_payment: 0, junk: 0 };
    for (const o of orders) {
      if (o.payment_status === "paid") c.paid += 1;
      if (o.payment_status === "awaiting_payment") c.awaiting_payment += 1;
      if (["cancelled", "expired", "failed"].includes(o.payment_status)) c.junk += 1;
    }
    return c;
  }, [orders]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/orders/${id}/status`,
        { status },
        { headers: authHeaders }
      );
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success(`Status: ${statusMeta(status).label}`);
    } catch {
      toast.error("Status wijzigen mislukt");
    }
  };

  const deleteOrder = async (o, e) => {
    if (e) e.stopPropagation();
    if (
      !window.confirm(
        `Weet je zeker dat je bestelling #${o.id.slice(0, 8).toUpperCase()} (${o.full_name}, € ${o.total.toFixed(2)}) volledig wilt verwijderen?`
      )
    )
      return;
    setDeletingId(o.id);
    try {
      await axios.delete(`${API}/admin/orders/${o.id}`, { headers: authHeaders });
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      toast.success("Bestelling verwijderd");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
    } finally {
      setDeletingId(null);
    }
  };

  const cleanupJunk = async () => {
    if (
      !window.confirm(
        "Alle onbetaalde bestellingen (geannuleerd, verlopen, mislukt) en > 30 min openstaande betalingen worden permanent verwijderd. Doorgaan?"
      )
    )
      return;
    setCleaning(true);
    try {
      const r = await axios.post(`${API}/admin/orders/cleanup`, {}, { headers: authHeaders });
      toast.success(`${r.data.deleted_count} bestelling(en) opgeruimd`);
      load();
    } catch {
      toast.error("Opruimen mislukt");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div data-testid="admin-orders-page" className="space-y-4">
      {/* Header + cleanup */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Package size={14} className="text-[var(--tha-red)]" />
          <span className="text-xs font-mono-x tracking-widest text-white">
            BESTELLINGEN ({orders.length})
          </span>
        </div>
        <button
          data-testid="cleanup-junk-orders"
          onClick={cleanupJunk}
          disabled={cleaning || counts.junk === 0}
          className="btn-ghost-red px-3 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-40"
          title="Verwijder alle geannuleerde, verlopen, mislukte en oude awaiting bestellingen"
        >
          <BrushCleaning size={12} /> {cleaning ? "BEZIG..." : `RUIM OP (${counts.junk})`}
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 text-xs font-mono-x tracking-widest">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-2 border transition-colors ${
              filter === f.key
                ? "border-[var(--tha-red)] text-[var(--tha-red)] bg-[#1a0303]"
                : "border-[var(--tha-border)] text-[var(--tha-muted)] hover:text-white"
            }`}
          >
            {f.label.toUpperCase()} ({f.key === "all" ? counts.all : counts[f.key] || 0})
          </button>
        ))}
      </div>

      <div className="border border-[var(--tha-border)] bg-[#0a0a0a]">
        {loading ? (
          <div className="p-12 text-center text-sm text-[var(--tha-muted)]">Laden...</div>
        ) : filtered.length === 0 ? (
          <div data-testid="admin-orders-empty" className="p-12 text-center text-sm text-[var(--tha-muted)]">
            Geen bestellingen in deze filter.
          </div>
        ) : (
          <ul>
            {filtered.map((o) => {
              const isOpen = expanded === o.id;
              const meta = statusMeta(o.status);
              const pmeta =
                PAYMENT_STATUS_META[o.payment_status] || PAYMENT_STATUS_META.awaiting_payment;
              return (
                <li
                  key={o.id}
                  data-testid={`order-row-${o.id}`}
                  className="border-b border-[var(--tha-border)] last:border-0"
                >
                  <div className="w-full px-5 py-4 flex items-center gap-3 hover:bg-[#111]">
                    <button
                      onClick={() => setExpanded(isOpen ? null : o.id)}
                      className="flex items-center gap-4 flex-1 min-w-0 text-left"
                    >
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-5 gap-2 items-center">
                        <div className="font-mono-x text-xs text-[var(--tha-red)]">
                          #{o.id.slice(0, 8).toUpperCase()}
                        </div>
                        <div className="text-sm text-white truncate flex items-center gap-2">
                          {o.full_name}
                          {o.pickup && (
                            <span
                              data-testid={`pickup-badge-${o.id}`}
                              className="text-[9px] font-mono-x tracking-widest px-1.5 py-0.5 border border-[var(--tha-red)] text-[var(--tha-red)]"
                              title="Klant wil ophalen in Assen — neem contact op"
                            >
                              OPHALEN
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[var(--tha-muted)] truncate">
                          {new Date(o.created_at).toLocaleString("nl-NL")}
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            data-testid={`payment-status-${o.id}`}
                            className="text-[10px] font-mono-x tracking-widest px-2 py-1"
                            style={{ background: `${pmeta.color}22`, color: pmeta.color }}
                            title="Betaalstatus (Mollie)"
                          >
                            {pmeta.label}
                          </span>
                          <span
                            className="text-[10px] font-mono-x tracking-widest uppercase px-2 py-1"
                            style={{ background: `${meta.color}22`, color: meta.color }}
                            title="Fulfillment-status"
                          >
                            {meta.label}
                          </span>
                        </div>
                        <div className="text-right font-display text-base text-white">
                          € {o.total.toFixed(2)}
                        </div>
                      </div>
                    </button>
                    <button
                      data-testid={`delete-order-${o.id}`}
                      onClick={(e) => deleteOrder(o, e)}
                      disabled={deletingId === o.id}
                      className="shrink-0 inline-flex items-center justify-center w-9 h-9 border border-[var(--tha-border)] text-[var(--tha-muted)] hover:border-[var(--tha-red)] hover:text-[var(--tha-red)] disabled:opacity-50"
                      title="Verwijder deze bestelling"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {isOpen && (
                    <div className="px-5 pb-5 bg-[#070707]">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                        <div>
                          <Title>KLANT</Title>
                          <KV k="Naam" v={o.full_name} />
                          <KV k="E-mail" v={o.email} />
                          <KV k="Telefoon" v={o.phone || "—"} />
                        </div>
                        <div>
                          <Title>{o.pickup ? "OPHALEN IN ASSEN" : "BEZORGADRES"}</Title>
                          {o.pickup ? (
                            <div className="text-sm text-white">
                              <span className="text-[var(--tha-red)] font-mono-x text-xs tracking-widest">
                                NEEM CONTACT OP
                              </span>
                              <br />
                              Tel: {o.phone || "—"}
                            </div>
                          ) : (
                            <div className="text-sm text-white">
                              {o.street} {o.house_number}
                              <br />
                              {o.postal_code} {o.city}
                            </div>
                          )}
                          {o.notes && (
                            <>
                              <Title className="mt-3">OPMERKINGEN</Title>
                              <div className="text-sm text-[var(--tha-muted)]">{o.notes}</div>
                            </>
                          )}
                        </div>
                      </div>

                      <Title className="mt-6">PRODUCTEN</Title>
                      <div className="border border-[var(--tha-border)]">
                        {o.items.map((i, idx) => (
                          <div
                            key={idx}
                            className="px-4 py-3 flex justify-between text-sm border-b border-[var(--tha-border)] last:border-0"
                          >
                            <span className="text-white">
                              {i.quantity}× {i.name}
                            </span>
                            <span className="text-[var(--tha-muted)]">
                              € {(i.price * i.quantity).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>

                      {o.payment_status === "paid" && (
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                          <span className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                            STATUS WIJZIGEN:
                          </span>
                          {STATUSES.map((s) => (
                            <button
                              key={s.value}
                              data-testid={`status-btn-${o.id}-${s.value}`}
                              onClick={() => updateStatus(o.id, s.value)}
                              className={`px-3 py-2 text-xs font-mono-x tracking-widest uppercase border transition-colors ${
                                o.status === s.value
                                  ? "border-[var(--tha-red)] text-[var(--tha-red)]"
                                  : "border-[var(--tha-border)] text-[var(--tha-muted)] hover:text-white hover:border-white"
                              }`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                      )}
                      {o.payment_status !== "paid" && (
                        <p className="mt-4 text-xs text-[var(--tha-muted)] italic">
                          Status-wijziging is alleen mogelijk na een geslaagde betaling.
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const Title = ({ children, className = "" }) => (
  <div className={`text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-2 ${className}`}>
    {children}
  </div>
);
const KV = ({ k, v }) => (
  <div className="text-sm">
    <span className="text-[var(--tha-muted)]">{k}: </span>
    <span className="text-white">{v}</span>
  </div>
);

export default AdminOrders;
